/**
 * QUOTATION ITEM MAPPERS — le righe del preventivo, lato prodotto.
 *
 * TS-87. La riga porta i campi comuni a tutti i clienti — posizione, quantità,
 * importo, note — mentre *cosa* si sta preventivando (prodotto, codice di
 * catalogo, certificazione) è materia di estensione e vive nel plugin di chi ne
 * ha bisogno.
 *
 * ## Perché non c'è nessun enum da mappare
 *
 * Perché non ce ne sono. È la conferma che il taglio fra core ed estensione è
 * caduto nel punto giusto: i vocabolari — stati del preventivo, tipi di
 * allegato, tipologie di intervento — descrivono *classificazioni*, e sono tutti
 * o del prodotto o del cliente. Una riga invece è aritmetica, e l'aritmetica non
 * ha dialetti.
 *
 * ## L'importo è la ragione per cui le righe stanno nel core
 *
 * Il totale del preventivo è la somma delle righe, e la ricalcola il servizio
 * core a ogni scrittura (`recalculateQuotationAmount`). Se le righe fossero del
 * plugin, il totale di un'entità di prodotto dipenderebbe da una tabella che il
 * prodotto non conosce.
 */

import { pickAuditFields, type AuditFields } from '@crm/shared';

/** Come arriva da `GET /quotation/{id}/item`. */
export interface QuotationItemDto extends AuditFields {
  quotationItemId: number;
  quotationId: number;
  position: number;
  quantity: number;
  amount: number;
  notes: string | null;
}

/** Come la usa la UI. */
export interface QuotationItem extends AuditFields {
  quotationItemId: number;
  quotationId: number;
  position: number;
  quantity: number;
  amount: number;
  notes: string;
}

/**
 * Corpo di create e update. In aggiornamento i campi assenti mantengono il
 * valore corrente: è il backend a fare merge, non il client a rileggere.
 *
 * `position` a zero in creazione significa «in fondo»: il servizio core assegna
 * la prossima posizione libera. Non è un valore magico inventato qui, è il
 * comportamento dichiarato di `addQuotationItem`.
 */
export interface QuotationItemInput {
  position?: number;
  quantity?: number;
  amount?: number;
  notes?: string | null;
}

export function quotationItemFromDto(dto: QuotationItemDto): QuotationItem {
  return {
    quotationItemId: dto.quotationItemId,
    quotationId: dto.quotationId,
    position: dto.position,
    quantity: dto.quantity,
    amount: dto.amount,
    notes: dto.notes ?? '',
    ...pickAuditFields(dto),
  };
}

export function quotationItemToInput(
  patch: Partial<Pick<QuotationItem, 'position' | 'quantity' | 'amount' | 'notes'>>,
): QuotationItemInput {
  const input: QuotationItemInput = {};
  if (patch.position !== undefined) input.position = patch.position;
  if (patch.quantity !== undefined) input.quantity = patch.quantity;
  if (patch.amount !== undefined) input.amount = patch.amount;
  if (patch.notes !== undefined) input.notes = patch.notes;
  return input;
}
